import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Sparkles, RefreshCw, LogOut, Settings, Clock, AlertTriangle } from 'lucide-react';

/**
 * Full-screen prompt shown to users whose subscription is `canceled`.
 *
 * 30-day cold-storage window:
 *   • If backend reports data_recovery_days_left > 0 → data is preserved;
 *     resubscribing anywhere in this window fully restores portfolio,
 *     collabs, chat, try-on history.
 *   • If the window has passed the account is wiped by the background task
 *     (user record deleted) — they can't reach this component at all.
 *
 * Users pick ANY plan when they resubscribe — no locked selection, no
 * pre-fill of previous plan.
 */

const ALLOWED_PATHS = [
  '/subscribe',
  '/checkout',
  '/subscription',
  '/settings',
];

const PUBLIC_PATHS = [
  '/', '/login', '/register', '/forgot-password', '/reset-password',
  '/privacy', '/privacy-policy', '/terms', '/terms-of-service',
  '/refund-policy', '/biometric-policy', '/acceptable-use',
  '/about', '/faq', '/contact', '/blog',
];

function isAllowed(pathname) {
  if (ALLOWED_PATHS.some((p) => pathname === p || pathname.startsWith(p + '/'))) return true;
  if (PUBLIC_PATHS.some((p) => pathname === p || pathname.startsWith(p + '/'))) return true;
  if (pathname.startsWith('/admin')) return true;
  return false;
}

export default function ResubscribeWall() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  if (!token || !user) return null;
  if (user.subscription_status !== 'canceled') return null;
  if (isAllowed(location.pathname)) return null;

  const daysLeft = user.data_recovery_days_left;
  const withinWindow = typeof daysLeft === 'number' && daysLeft > 0;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-md p-4 overflow-y-auto"
      data-testid="resubscribe-wall-overlay"
      data-recovery-days={daysLeft ?? ''}
      role="dialog"
      aria-modal="true"
      aria-labelledby="resubscribe-title"
    >
      <div className="bg-white w-full max-w-lg rounded-3xl shadow-2xl overflow-hidden my-8" data-testid="resubscribe-wall-card">
        <div className="bg-gradient-to-r from-rose-500 via-orange-500 to-amber-500 text-white px-6 py-6 flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-6 h-6" />
          </div>
          <div>
            <h1 id="resubscribe-title" className="text-xl font-bold leading-tight">
              {withinWindow ? 'Welcome back — ready to resubscribe?' : 'Your subscription has ended'}
            </h1>
            <p className="text-white/90 text-sm">
              {withinWindow ? 'Your data is safely held for a limited time.' : 'Choose any plan to get started again.'}
            </p>
          </div>
        </div>

        <div className="px-6 py-6 space-y-5">
          {withinWindow ? (
            <>
              <div
                className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3"
                data-testid="recovery-countdown-banner"
              >
                <Clock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-900">
                  <p className="font-semibold">
                    {daysLeft} {daysLeft === 1 ? 'day' : 'days'} left to keep your data
                  </p>
                  <p className="text-amber-800">
                    Resubscribe to any plan within this window and your portfolio, try-on history, collaborations and chat messages are restored, untouched.
                  </p>
                </div>
              </div>

              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                <p className="text-sm text-emerald-800">
                  <strong>Choose any tier within your account type</strong> — upgrade, downgrade or stay on the same plan. Data recovery is unaffected by plan choice.
                </p>
              </div>
            </>
          ) : (
            <div
              className="bg-rose-50 border border-rose-200 rounded-xl p-4 flex items-start gap-3"
              data-testid="recovery-expired-banner"
            >
              <AlertTriangle className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-rose-900">
                <p className="font-semibold">Data recovery window has closed.</p>
                <p className="text-rose-800">
                  We were unable to preserve your previous portfolio and collaborations. You can still resubscribe and start fresh.
                </p>
              </div>
            </div>
          )}

          <button
            onClick={() => navigate('/subscribe')}
            data-testid="resubscribe-primary-btn"
            className="w-full bg-gradient-to-r from-rose-500 to-orange-500 hover:from-rose-600 hover:to-orange-600 text-white py-3.5 rounded-xl font-semibold flex items-center justify-center gap-2 shadow-md text-base"
          >
            <RefreshCw className="w-5 h-5" />
            Choose a Plan &amp; Resubscribe
          </button>

          <button
            onClick={() => navigate('/settings')}
            data-testid="resubscribe-settings-btn"
            className="w-full bg-white border border-gray-200 hover:border-rose-300 text-gray-600 py-2.5 rounded-xl font-medium text-sm flex items-center justify-center gap-2"
          >
            <Settings className="w-4 h-4" />
            Account Settings
          </button>

          <div className="pt-4 border-t border-gray-100 flex items-center justify-between text-xs">
            <button
              onClick={handleLogout}
              data-testid="resubscribe-logout-btn"
              className="text-gray-500 hover:text-gray-700 flex items-center gap-1.5"
            >
              <LogOut className="w-3.5 h-3.5" />
              Log out
            </button>
            <a
              href="mailto:help@virtualfit.work"
              className="text-gray-500 hover:text-rose-500"
              data-testid="resubscribe-support-link"
            >
              Need help? help@virtualfit.work
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
